import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  Banner,
  Box,
  Button,
  Divider,
  Grid,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalFooter,
  SegmentedControl,
  SkeletonShape,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import { useEffect, useMemo, useState } from 'react';
import { useAnalytics } from '@/services/analytics';

import { setPlumaModalSize } from '../set-pluma-modal-size';
import { ImageComparison } from '../components/image-comparison';
import { MAX_FILE_SIZE, MAX_FILE_SIZE_MB, OPTIMIZATION_THRESHOLD_SIZE } from '../utils';
import * as styles from './asset-optimizer-modal.css';
import {
  type CompressionMode,
  type CompressionType,
  useAssetOptimizerModal,
  useOptimizeAsset,
  useOriginalAsset,
  useSaveOptimizedAsset,
} from './hooks';

type ViewType = 'before' | 'after' | 'side-by-side';
type SideBySideDirection = 'horizontal' | 'vertical';

function useBlobUrl(blob: Blob | undefined | null): string | null {
  const url = useMemo(() => (blob != null ? URL.createObjectURL(blob) : null), [blob]);

  useEffect(() => {
    return () => {
      if (url != null) URL.revokeObjectURL(url);
    };
  }, [url]);

  return url;
}

export function AssetOptimizerModal() {
  const { isOpen, close } = useAssetOptimizerModal();

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      aria-label="Image Optimizer"
      shouldShowCloseButton
      title="Asset Optimizer"
      style={setPlumaModalSize({ maxWidth: '1000px' })}
    >
      {isOpen ? <OptimizerModalContent /> : null}
    </Modal>
  );
}

function OptimizerModalContent() {
  const { track } = useAnalytics();
  const { state, close } = useAssetOptimizerModal();
  const { saveOptimizedAsset, isUploading } = useSaveOptimizedAsset();
  const originalAssetState = useOriginalAsset({
    originalAsset: state?.originalAsset,
  });

  const [viewType, setViewType] = useState<ViewType>('side-by-side');
  const [sideBySideDirection, setSideBySideDirection] = useState<SideBySideDirection>('horizontal');
  const [compressionType, setCompressionType] = useState<CompressionType>('regular');
  const [compressionMode, setCompressionMode] = useState<CompressionMode>('normal');

  const optimizedAssetState = useOptimizeAsset({
    originalAssetPath: state?.originalAsset?.path,
    originalAssetBlob: originalAssetState?.blob ?? null,
    compressionType,
    compressionMode,
  });

  const originalUrl = useBlobUrl(originalAssetState?.blob);
  const optimizedUrl = useBlobUrl(optimizedAssetState?.blob);

  const isLoading = optimizedAssetState?.isOptimizing == null || optimizedAssetState?.isOptimizing === true;

  const hasResult = !isLoading && !optimizedAssetState?.isError;
  const isOverSizeLimit = hasResult && (optimizedAssetState?.blob?.size ?? 0) > MAX_FILE_SIZE;
  const isStillBig = hasResult && (optimizedAssetState?.blob?.size ?? 0) > OPTIMIZATION_THRESHOLD_SIZE;

  return (
    <>
      <ModalBody>
        <Grid columns="2fr 1fr" gap="200">
          <Box display="flex" justifyContent="center" flexGrow="1">
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center">
                <SkeletonShape
                  style={{
                    aspectRatio:
                      originalAssetState?.details != null
                        ? `${originalAssetState.details.width} / ${originalAssetState.details.height}`
                        : '1 / 1',
                    maxHeight: '65vh',
                    width: 'auto',
                    maxWidth: '100%',
                    height: '65vh',
                    borderRadius: themeVars.radius['3'],
                  }}
                />
              </Box>
            ) : optimizedAssetState?.isError === true ? (
              <Box className={styles.errorPreviewContainer}>
                {originalUrl != null ? (
                  <Image src={originalUrl} alt="Original" className={styles.errorPreviewImage} />
                ) : null}
              </Box>
            ) : originalUrl != null && optimizedUrl != null ? (
              <ImageComparison
                type={viewType}
                direction={sideBySideDirection}
                height="65vh"
                beforeUrl={originalUrl}
                beforeLabel="Original"
                afterUrl={optimizedUrl}
                afterLabel="Optimized"
              />
            ) : null}
          </Box>
          <Box display="flex" flexDirection="column" mx="250">
            <Box display="flex" flexDirection="row" mb="300" alignItems="flex-end">
              <SegmentedControl
                options={[
                  { label: 'Before', value: 'before' },
                  { label: 'After', value: 'after' },
                  { label: 'Side-by-side', value: 'side-by-side' },
                ]}
                value={viewType}
                label="View type"
                onChange={(viewType: string) => setViewType(viewType as ViewType)}
              />
              <DirectionToggle
                value={sideBySideDirection}
                onChange={(direction) => setSideBySideDirection(direction)}
                hidden={viewType !== 'side-by-side'}
              />
            </Box>

            <Box display="flex" flexDirection="column" mb="300">
              <SegmentedControl
                label="Compression type"
                options={[
                  { label: 'Regular', value: 'regular' },
                  { label: 'Good', value: 'good' },
                  { label: 'Maximum', value: 'maximum' },
                ]}
                value={compressionType}
                onChange={(v: string) => setCompressionType(v as CompressionType)}
              />
            </Box>

            <Box display="flex" flexDirection="column" mb="300">
              <Box display="flex">
                <SegmentedControl
                  label="Retina mode"
                  options={[
                    { label: 'Yes', value: 'retina' },
                    { label: 'No', value: 'normal' },
                  ]}
                  value={compressionMode}
                  onChange={(v: string) => setCompressionMode(v as CompressionMode)}
                />
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" gap="150">
              <Box display="flex" justifyContent="space-between">
                <Text text="product-ps">Original size</Text>
                <Text text="product-ps-active">{originalAssetState?.details?.size ?? '—'}</Text>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Text text="product-ps">Optimized size</Text>
                <Text text="product-ps-active">{isLoading ? '...' : optimizedAssetState?.details?.size ?? '—'}</Text>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between">
                <Text text="product-ps">Savings</Text>
                <Text
                  text="product-ps-active"
                  color={(optimizedAssetState?.details?.sizeDeltaPercent ?? 0) < 0 ? 'critical' : 'success'}
                >
                  {isLoading ? '...' : optimizedAssetState?.details?.sizeDelta ?? '—'}
                </Text>
              </Box>
            </Box>
            {optimizedAssetState?.isError ? (
              <Box mt="200">
                <Banner variant="error">Failed to optimize image. Please try a different compression setting.</Banner>
              </Box>
            ) : isOverSizeLimit ? (
              <Box mt="200">
                <Banner variant="error">
                  The optimized image exceeds the maximum file size limit ({MAX_FILE_SIZE_MB} MB). Please try a higher
                  compression setting.
                </Banner>
              </Box>
            ) : (
              isStillBig &&
              compressionType !== 'maximum' && (
                <Box mt="200">
                  <Banner variant="caution">
                    The optimized image still has a substantial file size. You can try increasing the compression type
                    to reduce the file size further.
                  </Banner>
                </Box>
              )
            )}
          </Box>
        </Grid>
      </ModalBody>
      <ModalFooter>
        <Button variant="subtle" onClick={() => close()} isDisabled={isUploading}>
          Cancel
        </Button>
        <Button
          ml="auto"
          variant="primary"
          isDisabled={isUploading || optimizedUrl == null || isOverSizeLimit || optimizedAssetState?.isError === true}
          onClick={async () => {
            if (optimizedAssetState?.blob == null) return;

            await saveOptimizedAsset(optimizedAssetState.blob);

            track('assetOptimizer', 'saveOptimizedImage', {
              view_type: viewType,
              compression_type: compressionType,
              retina_mode: compressionMode === 'retina' ? 'yes' : 'no',
              'Builder Type': 'design-studio',
              original_size: originalAssetState?.details?.size,
              optimized_size: optimizedAssetState?.details?.size,
              savings_percentage: optimizedAssetState?.details?.sizeDeltaPercent,
            });
          }}
        >
          Save optimized image
        </Button>
      </ModalFooter>
    </>
  );
}

type DirectionToggleProps = {
  value: SideBySideDirection;
  onChange: (value: SideBySideDirection) => void;
  hidden?: boolean;
};

const DirectionToggle = ({ value, onChange, hidden = false }: DirectionToggleProps) => {
  if (hidden) return null;

  const tooltipContent = value === 'horizontal' ? 'Toggle side-by-side vertical' : 'Toggle side-by-side horizontal';

  return (
    <Tooltip isCustom content={tooltipContent}>
      {(context) => (
        <Button
          {...context.reference.getReferenceProps()}
          ref={context.reference.setReference}
          variant="subtle"
          isIconOnly
          aria-label={tooltipContent}
          onClick={() => onChange(value === 'horizontal' ? 'vertical' : 'horizontal')}
          ml="100"
        >
          <Icon name="swap" />
        </Button>
      )}
    </Tooltip>
  );
};
